import "@arco-design/web-react/es/TimePicker/style";
import _TimePicker from "@arco-design/web-react/es/TimePicker";
import "@arco-design/web-react/es/DatePicker/style";
import _DatePicker from "@arco-design/web-react/es/DatePicker";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import { useEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
function normalizePicker(picker) {
  if (!picker || picker === 'undefined') {
    return undefined;
  }
  return picker;
}
function toDayjsValue(value, inputFormat) {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (dayjs.isDayjs(value)) {
    return value;
  }
  const parsed = typeof value === 'string' ? dayjs(value, inputFormat) : dayjs(value);
  return parsed.isValid() ? parsed : undefined;
}
function toRangeDayjsValue(value, inputFormat) {
  if (!Array.isArray(value) || value.length < 2) {
    return undefined;
  }
  const start = toDayjsValue(value[0], inputFormat);
  const end = toDayjsValue(value[1], inputFormat);
  if (!start || !end) {
    return undefined;
  }
  return [start, end];
}
function formatOutputValue(value, inputFormat) {
  return value ? value.format(inputFormat) : undefined;
}
function formatDatePickerOutputValue(value, inputFormat) {
  if (!value) {
    return undefined;
  }
  if (inputFormat === 'YYYY-MM-DD HH:mm:ss') {
    const now = dayjs();
    return value.hour(now.hour()).minute(now.minute()).second(now.second()).format(inputFormat);
  }
  return value.format(inputFormat);
}
function getDefaultDatePickerValue(inputFormat) {
  if (inputFormat === 'YYYY-MM-DD HH:mm:ss') {
    return dayjs();
  }
  return undefined;
}
function formatRangeOutputValue(values, inputFormat) {
  if (!values || values.length < 2) {
    return [];
  }
  const hasTimeFormat = inputFormat.includes('HH') || inputFormat.includes('mm') || inputFormat.includes('ss');
  if (hasTimeFormat) {
    return [values[0].startOf('day').format(inputFormat), values[1].endOf('day').format(inputFormat)];
  }
  return [values[0].format(inputFormat), values[1].format(inputFormat)];
}
function useInitialSingleChange(value, inputFormat, onChange, name, formatter = formatOutputValue) {
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;
    const initialValue = toDayjsValue(value, inputFormat) ?? getDefaultDatePickerValue(inputFormat);
    const formatted = formatter(initialValue, inputFormat);
    if (formatted !== undefined) {
      onChange?.(formatted, formatted, name);
    }
  }, [formatter, inputFormat, name, onChange, value]);
}
function useInitialRangeChange(value, inputFormat, onChange, name) {
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;
    const formatted = formatRangeOutputValue(toRangeDayjsValue(value, inputFormat), inputFormat);
    if (formatted.length) {
      onChange?.(formatted, formatted, name);
    }
  }, [inputFormat, name, onChange, value]);
}
function DatePicker({
  style,
  bordered,
  readOnly,
  value,
  onChange,
  inputFormat = 'YYYY-MM-DD',
  format = 'YYYY-MM-DD',
  placeholder,
  disabled = false,
  showTime,
  disabledDate,
  picker,
  size,
  name,
  ...rest
}) {
  void bordered;
  useInitialSingleChange(value, inputFormat, onChange, name, formatDatePickerOutputValue);
  const transformedValue = toDayjsValue(value, inputFormat);
  const [innerValue, setInnerValue] = useState(transformedValue ?? getDefaultDatePickerValue(inputFormat));
  // 外部 value 变 undefined 时同步清空 innerValue，否则 Arco DatePicker 会继续显示旧值。
  useEffect(() => {
    if (value === undefined || value === null || value === '') {
      setInnerValue(undefined);
    }
  }, [value]);
  const normalizedPicker = normalizePicker(picker);
  const currentValue = value === undefined ? innerValue : transformedValue;

  // useEffect(() => {
  //   if (value !== undefined) {
  //     setInnerValue(transformedValue);
  //     return;
  //   }

  //   setInnerValue(undefined);
  // }, [inputFormat, transformedValue, value]);

  const commonProps = {
    ...rest,
    style,
    format,
    placeholder,
    disabled,
    disabledDate,
    showTime,
    size,
    editable: readOnly ? false : rest.editable,
    value: currentValue,
    onChange: (_dateString, date) => {
      if (value === undefined) {
        setInnerValue(date);
      }
      const nextValue = formatDatePickerOutputValue(date, inputFormat);
      onChange?.(nextValue, nextValue, name);
    }
  };
  if (normalizedPicker === 'month') {
    return /*#__PURE__*/React.createElement(_DatePicker.MonthPicker, commonProps);
  }
  if (normalizedPicker === 'quarter') {
    return /*#__PURE__*/React.createElement(_DatePicker.QuarterPicker, commonProps);
  }
  if (normalizedPicker === 'week') {
    return /*#__PURE__*/React.createElement(_DatePicker.WeekPicker, commonProps);
  }
  if (normalizedPicker === 'year') {
    return /*#__PURE__*/React.createElement(_DatePicker.YearPicker, commonProps);
  }
  return /*#__PURE__*/React.createElement(_DatePicker, commonProps);
}
function TimePicker({
  style,
  bordered,
  value,
  onChange,
  inputFormat = 'HH:mm',
  format = 'HH:mm',
  placeholder,
  disabled = false,
  size,
  name,
  readOnly,
  ...rest
}) {
  void bordered;
  useInitialSingleChange(value, inputFormat, onChange, name);
  const transformedValue = toDayjsValue(value, inputFormat);
  const [innerValue, setInnerValue] = useState(transformedValue);
  // 外部 value 变 undefined 时同步清空 innerValue，否则 Arco TimePicker 会继续显示旧值。
  useEffect(() => {
    if (value === undefined || value === null || value === '') {
      setInnerValue(undefined);
    }
  }, [value]);
  const currentValue = value === undefined ? innerValue : transformedValue;
  return /*#__PURE__*/React.createElement(_TimePicker, _extends({}, rest, {
    size: size,
    style: style,
    format: format,
    placeholder: placeholder,
    value: currentValue,
    onChange: (_valueString, date) => {
      if (value === undefined) {
        setInnerValue(date);
      }
      const nextValue = formatOutputValue(date, inputFormat);
      onChange?.(nextValue, nextValue, name);
    },
    editable: readOnly ? false : rest.editable,
    disabled: disabled
  }));
}
function RangePicker({
  style,
  bordered,
  readOnly,
  value,
  onChange,
  inputFormat = 'YYYY-MM-DD',
  format = 'YYYY-MM-DD',
  placeholder,
  showTime,
  disabled = false,
  disabledDate,
  size,
  name,
  ...rest
}) {
  void bordered;
  useInitialRangeChange(value, inputFormat, onChange, name);
  const valueKey = Array.isArray(value) ? value.join(',') : String(value ?? '__undefined__');
  const transformedValue = useMemo(() => toRangeDayjsValue(value, inputFormat),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [valueKey, inputFormat]);
  const [innerValue, setInnerValue] = useState(transformedValue);
  // 外部 value 变 undefined（如父级清空、setFieldsValue('')）时同步清空 innerValue，
  // 否则 Arco RangePicker 会继续显示旧值。
  useEffect(() => {
    if (value === undefined) {
      setInnerValue(undefined);
    }
  }, [value]);
  const currentValue = value === undefined ? innerValue : transformedValue;
  return /*#__PURE__*/React.createElement(_DatePicker.RangePicker, _extends({}, rest, {
    size: size,
    style: style,
    format: format,
    disabled: disabled,
    disabledDate: disabledDate,
    showTime: showTime,
    placeholder: placeholder,
    value: currentValue,
    editable: readOnly ? false : rest.editable,
    onChange: (_valueString, date) => {
      if (value === undefined) {
        setInnerValue(date);
      }
      const nextValue = formatRangeOutputValue(date, inputFormat);
      onChange?.(nextValue, nextValue, name);
    }
  }));
}
function TaskRangePicker(props) {
  const {
    value,
    inputFormat = 'YYYY-MM-DD',
    onChange,
    name
  } = props;
  const [duration, setDuration] = useState({
    day: 0,
    hour: 0
  });
  const initializedRef = useRef(false);
  function updateDuration(dateRange) {
    if (!dateRange || dateRange.length < 2) {
      setDuration({
        day: 0,
        hour: 0
      });
      return;
    }
    const totalHours = dateRange[1].diff(dateRange[0], 'hours');
    setDuration({
      day: Math.floor(totalHours / 24),
      hour: totalHours % 24
    });
  }
  useEffect(() => {
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;
    const dateRange = toRangeDayjsValue(value, inputFormat);
    if (!dateRange) {
      return;
    }
    const nextValue = formatRangeOutputValue(dateRange, inputFormat);
    onChange?.(nextValue, nextValue, name);
    updateDuration(dateRange);
  }, [inputFormat, name, onChange, value]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RangePicker, _extends({}, props, {
    onChange: (nextValue, item, fieldName) => {
      const dateRange = toRangeDayjsValue(nextValue, inputFormat);
      updateDuration(dateRange);
      onChange?.(nextValue, item, fieldName);
    },
    shortcuts: [{
      text: '全天',
      value: () => [dayjs().startOf('day'), dayjs().endOf('day')]
    }]
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 8,
      color: 'rgb(var(--gray-8))',
      fontSize: 12,
      lineHeight: '20px'
    }
  }, duration.day, " ", /*#__PURE__*/React.createElement("span", {
    style: {
      color: 'rgb(var(--gray-6))'
    }
  }, "\u5929"), ' ', duration.hour, ' ', /*#__PURE__*/React.createElement("span", {
    style: {
      color: 'rgb(var(--gray-6))'
    }
  }, "\u5C0F\u65F6")));
}
export { DatePicker, RangePicker, TaskRangePicker, TimePicker };
export default DatePicker;